import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	OnInit,
	Output,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TextInputComponent),
	multi: true,
};

@Component({
	selector: "pv-input",
	styleUrls: ["input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TextInputComponent implements ControlValueAccessor, OnInit {
	private cdRef = inject(ChangeDetectorRef);

	@Input() type: string;
	@Input() label: string;
	@Input() placeholder = "";
	@Input() icon: string;
	@Input() indicator: string;
	@Input() inputClass = "";
	@Input() disabled = false;
	@Input() minLength: string;
	@Input() maxLength: string;
	@Input() ssn = false;
	@Input() dateTime = false;
	@Input() required = true;
	@Input() isRequired = true;
	@Input() help: string;
	@Input() autocomplete = "off";
	@Input() autocapitalize: string;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() suffix: string;
	// Note: The mask input does not currently support sending the raw value without the mask in the form value. Therefore,
	// if you want to send the raw value, you will need to modify the masked value input before sending the data. This is a
	// known issue and we are waiting for a fix from the ngx-mask and Angular.
	@Input() mask: string;
	@Input() isPending: boolean;

	@Input() wizardIcon = "fa fa-magic";
	@Input() pasteTooltip: string;
	@Input() wizardTooltip: string;
	@Output() openWizard = new EventEmitter<void>();
	@Output() pasteValue = new EventEmitter<void>();
	@Output() blurEvent = new EventEmitter<void>();

	value: any = "";
	pendingIcon = "fa fa-spin fa-refresh";

	private onTouch = () => {};
	private onModelChange = (_: unknown) => {};

	writeValue(value: string | number) {
		this.value = value;
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	onChange(value: string) {
		this.value = value;
		let formValue: any = value;

		if (this.type === "number" && value !== "") {
			formValue = Number(value);
		} else if (this.type === "text") {
			formValue = value.trim();
		}
		this.onModelChange(formValue);
	}

	onTouched() {
		this.blurEvent.emit();
		this.onTouch();
	}

	ngOnInit() {
		if (this.type === "number") {
			console.warn('The use of type "number" is deprecated for pv-input. Please use ic-number-input instead.');
		}
	}
}
